// Phase NAATI-MAX B2 — /translate/naati/category/$slug (TH)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_CATEGORIES, getNaatiCategory, getCategoryDoctypes } from "@/data/naati-categories";
import HubLeadCTA from "@/components/HubLeadCTA";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/naati/category/$slug")({
  loader: ({ params }) => {
    const cat = getNaatiCategory(params.slug);
    if (!cat) throw notFound();
    return { cat, docs: getCategoryDoctypes(cat.slug) };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const { cat, docs } = loaderData;
    const url = `${SITE}/translate/naati/category/${params.slug}`;
    const enUrl = `/en/translate/naati/category/${params.slug}`;
    const title = `${cat.nameTh} — แปลรับรอง NAATI | NYC Language Institute`;
    const prices = docs.map((d) => d.priceFromThb);
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/category/$slug", "th") }, { name: "description", content: ctaDesc(cat.taglineTh, "/translate/naati/category/$slug", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/category/$slug", "th")}, { property: "og:url", content: url },
        { property: "og:description", content: ctaDesc(cat.taglineTh, "/translate/naati/category/$slug", "th")},
      ],
      links: canonicalAndHreflang(`/translate/naati/category/${params.slug}`, enUrl),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "Service",
          "@id": `${url}#service`, name: title, description: cat.taglineTh, inLanguage: "th",
          serviceType: "NAATI Certified Translation",
          provider: { "@type": "ProfessionalService", name: "NYC Language Institute", url: SITE },
          areaServed: { "@type": "Country", name: "Australia" },
          offers: prices.length ? {
            "@type": "AggregateOffer", priceCurrency: "THB",
            lowPrice: Math.min(...prices), highPrice: Math.max(...prices),
            offerCount: docs.length, availability: "https://schema.org/InStock",
          } : undefined,
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "OfferCatalog",
          name: cat.nameTh, numberOfItems: docs.length,
          itemListElement: docs.map((d) => ({
            "@type": "Offer", name: d.nameTh, category: d.category,
            priceSpecification: { "@type": "UnitPriceSpecification", price: d.priceFromThb, priceCurrency: "THB", unitText: "ต่อหน้า A4" },
            availability: "https://schema.org/InStock",
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "NAATI", item: `${SITE}/translate/naati` },
            { "@type": "ListItem", position: 3, name: cat.nameTh, item: url },
          ],
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#speakable`, url,
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
        }) },
      ],
    };
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { cat, docs } = Route.useLoaderData() as { cat: NonNullable<ReturnType<typeof getNaatiCategory>>; docs: ReturnType<typeof getCategoryDoctypes> };
  return (
    <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
      <nav className="mb-6 text-xs text-slate-500">
        <Link to="/" className="hover:text-amber-700">หน้าแรก</Link> / <Link to="/translate/naati" className="hover:text-amber-700">NAATI</Link> / <span>{cat.nameTh}</span>
      </nav>
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700" data-speakable="true">NAATI · {cat.nameEn}</div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">{cat.nameTh}</h1>
      <p className="mt-3 text-lg text-slate-700" data-speakable="true">{cat.taglineTh}</p>
      <p className="mt-4 text-base leading-relaxed text-slate-700">{cat.introTh}</p>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">วีซ่าและงานที่ใช้เอกสารกลุ่มนี้</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {cat.visaSubclassesTh.map((v, i) => (
            <li key={i} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">{v}</li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">เอกสารทั้งหมด ({docs.length} รายการ)</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3">เอกสาร</th>
                <th className="px-4 py-3">ราคา/หน้า</th>
                <th className="px-4 py-3">ระยะเวลา</th>
                <th className="px-4 py-3">สิ่งที่ต้องเตรียม</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.slug} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-navy-deep">{d.nameTh}<div className="text-xs font-normal text-slate-500">{d.nameEn}</div></td>
                  <td className="px-4 py-3">฿{d.priceFromThb.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs">{d.turnaround}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{d.notesTh}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">หมวด NAATI อื่นๆ</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {NAATI_CATEGORIES.filter((c) => c.slug !== cat.slug).map((c) => (
            <Link key={c.slug} to="/translate/naati/category/$slug" params={{ slug: c.slug }} className="rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-400">
              <div className="font-semibold text-navy-deep">{c.nameTh}</div>
              <div className="text-xs text-slate-500">{c.taglineTh}</div>
            </Link>
          ))}
        </div>
      </section>
      <HubLeadCTA locale="th" service="NAATI Category" />
      <QuotableStatsStrip cluster="naati" locale="th" variant="light" />
    </main>
  );
}
